/* Wat de organisatiepagina en de contactpagina lezen: de rij zelf en alles wat eraan
 * hangt, in losse vragen (geen embed, zodat één ontbrekend stuk de rest niet laat
 * vallen). Gearchiveerd is iets anders dan "bestaat niet": dan wijst de pagina naar het
 * Archief.
 *
 * Relatieve imports (geen @/), zodat de toetsen dit bestand zonder bundel lezen. */

import type { StatusVelden } from '../../offertes/data/status';
import { adressenUit, contactNaam, sorteerNaam, type Adres } from './labels';
import type { RelatieClient } from './organisaties';

export type RelatieFoutSoort = 'niet-gevonden' | 'gearchiveerd' | 'fout';

export class RelatieFout extends Error {
  constructor(public soort: RelatieFoutSoort, bericht: string, public gearchiveerdOp: string | null = null) {
    super(bericht);
    this.name = 'RelatieFout';
  }
}

export interface ContactKort {
  id: string;
  naam: string;
  functie: string | null;
  email: string | null;
  telefoon: string | null;
}

export interface ProjectKort {
  id: string;
  nummer: string | null;
  naam: string;
  stage: string | null;
  status: string | null;
  bedrag: number | null;
  plaats: string | null;
  bijgewerkt: string | null;
}

export interface OfferteKort extends StatusVelden {
  id: string;
  nummer: string | null;
  titel: string;
  bedrag: number | null;
  project_id: string | null;
  bijgewerkt: string | null;
}

export interface LocatieKort {
  id: string;
  nummer: string | null;
  naam: string;
  status: string | null;
  project_id: string | null;
}

export interface KansKort {
  id: string;
  naam: string;
  status: string | null;
  vloot: number | null;
  elektrisch: number | null;
}

export interface KavelKort {
  id: string;
  naam: string;
  fase: string | null;
  plaats: string | null;
}

export interface Organisatie {
  id: string;
  naam: string;
  sector: string | null;
  website: string | null;
  kvk: string | null;
  btw: string | null;
  telefoon: string | null;
  email: string | null;
  beschrijving: string | null;
  status: string | null;
  classificatie: string | null;
  logo: string | null;
  adressen: Adres[];
  zichtbaarVoor: string[];
  aangemaakt: string | null;
  bron: Record<string, any>;
}

export interface OrganisatiePaginaData {
  organisatie: Organisatie;
  contacten: ContactKort[];
  projecten: ProjectKort[];
  offertes: OfferteKort[];
  locaties: LocatieKort[];
  kansen: KansKort[];
  kavels: KavelKort[];
  piekanalyses: number;
}

const tekst = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const getal = (v: unknown) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const nummerVan = (s: string | null) => {
  const m = /(\d+)/.exec(s ?? '');
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
};

const CONTACT_KORT = 'id, first_name, last_name, title, email, phone, mobile';
const PROJECT_KORT = 'id, name, project_number, stage, status, amount, city, updated_at';
const OFFERTE_KORT = 'id, quote_number, title, status, total_amount, valid_until, sent_on, decided_on, quote_date, created_at, updated_at, project_id';

const alsContact = (r: any): ContactKort => ({
  id: r.id,
  naam: contactNaam(r.first_name, r.last_name),
  functie: tekst(r.title),
  email: tekst(r.email),
  telefoon: tekst(r.phone) ?? tekst(r.mobile),
});

const alsProject = (r: any): ProjectKort => ({
  id: r.id,
  nummer: tekst(r.project_number),
  naam: tekst(r.name) ?? '(zonder naam)',
  stage: r.stage ?? null,
  status: r.status ?? null,
  bedrag: getal(r.amount),
  plaats: tekst(r.city),
  bijgewerkt: r.updated_at ?? null,
});

const alsOfferte = (r: any): OfferteKort => ({
  id: r.id,
  nummer: tekst(r.quote_number),
  titel: tekst(r.title) ?? '(zonder titel)',
  status: r.status,
  bedrag: getal(r.total_amount),
  valid_until: r.valid_until ?? null,
  sent_on: r.sent_on ?? null,
  decided_on: r.decided_on ?? null,
  quote_date: r.quote_date ?? null,
  created_at: r.created_at ?? null,
  project_id: r.project_id ?? null,
  bijgewerkt: r.updated_at ?? null,
});

const opNummer = <T extends { nummer: string | null }>(a: T, b: T) => nummerVan(b.nummer) - nummerVan(a.nummer);
const opAchternaam = (rijen: any[]) =>
  [...rijen].sort((a, b) => sorteerNaam(a.last_name, a.first_name).localeCompare(sorteerNaam(b.last_name, b.first_name), 'nl'));

function controleer(...antwoorden: { error: any }[]) {
  for (const a of antwoorden) if (a.error) throw new RelatieFout('fout', a.error.message ?? String(a.error));
}

/** De offertes van een organisatie: op de organisatie zelf of op een van haar projecten. */
async function offertesVoor(client: RelatieClient, filters: { organisatie?: string; projecten?: string[]; contact?: string }): Promise<OfferteKort[]> {
  const vragen: Promise<any>[] = [];
  const basis = () => client.from('quotes').select(OFFERTE_KORT).is('deleted_at', null);
  if (filters.organisatie) vragen.push(basis().eq('organization_id', filters.organisatie));
  if (filters.projecten?.length) vragen.push(basis().in('project_id', filters.projecten));
  if (filters.contact) vragen.push(basis().eq('contact_id', filters.contact));
  const antwoorden = await Promise.all(vragen);
  controleer(...antwoorden);
  const uniek = new Map<string, OfferteKort>();
  for (const a of antwoorden) for (const r of a.data ?? []) uniek.set(r.id, alsOfferte(r));
  return [...uniek.values()].sort((a, b) => ((b.bijgewerkt ?? '') < (a.bijgewerkt ?? '') ? -1 : (b.bijgewerkt ?? '') > (a.bijgewerkt ?? '') ? 1 : 0));
}

export async function haalOrganisatie(client: RelatieClient, id: string): Promise<OrganisatiePaginaData> {
  const o = await client.from('organizations').select('*').eq('id', id).maybeSingle();
  if (o.error) {
    /* een id dat geen uuid is: dat bestaat niet */
    if (o.error.code === '22P02') throw new RelatieFout('niet-gevonden', `"${id}" is geen geldige organisatie.`);
    throw new RelatieFout('fout', o.error.message ?? String(o.error));
  }
  if (!o.data) throw new RelatieFout('niet-gevonden', 'Deze organisatie bestaat niet, of je mag hem niet zien.');
  if (o.data.deleted_at) throw new RelatieFout('gearchiveerd', `"${o.data.name}" is gearchiveerd. Je kunt hem terugzetten vanuit het Archief.`, o.data.deleted_at);

  const [contacten, projecten, kansen, kavels, piek] = await Promise.all([
    client.from('contacts').select(CONTACT_KORT).eq('org_id', id).is('deleted_at', null),
    client.from('projects').select(PROJECT_KORT).eq('org_id', id).is('deleted_at', null),
    client.from('charging_opportunities').select('id, name, status, total_fleet, electric_trucks').eq('org_id', id).is('deleted_at', null),
    client.from('charging_locations').select('id, name, stage, city').eq('org_id', id).is('deleted_at', null),
    client.from('piek_analyses').select('id').eq('organization_id', id),
  ]);
  controleer(contacten, projecten, kansen, kavels);

  const projectIds = (projecten.data ?? []).map((p: any) => p.id);
  const [offertes, locaties] = await Promise.all([
    offertesVoor(client, { organisatie: id, projecten: projectIds }),
    projectIds.length
      ? client.from('sites').select('id, name, site_number, status, project_id').in('project_id', projectIds).is('deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
  ]);
  controleer(locaties);

  const r = o.data;
  return {
    organisatie: {
      id: r.id,
      naam: tekst(r.name) ?? '(zonder naam)',
      sector: tekst(r.sector),
      website: tekst(r.website),
      kvk: tekst(r.kvk),
      btw: tekst(r.btw),
      telefoon: tekst(r.phone),
      email: tekst(r.email),
      beschrijving: tekst(r.description),
      status: r.status ?? null,
      classificatie: r.classification ?? null,
      logo: r.logo_url || r.cached_favicon_url || r.favicon_url || null,
      adressen: adressenUit(r.addresses),
      zichtbaarVoor: Array.isArray(r.visible_for_tenants) ? r.visible_for_tenants : [],
      aangemaakt: r.created_at ?? null,
      bron: r,
    },
    contacten: opAchternaam(contacten.data ?? []).map(alsContact),
    projecten: (projecten.data ?? []).map(alsProject).sort(opNummer),
    offertes,
    locaties: (locaties.data ?? [])
      .map((s: any): LocatieKort => ({ id: s.id, nummer: tekst(s.site_number), naam: tekst(s.name) ?? '(zonder naam)', status: s.status ?? null, project_id: s.project_id ?? null }))
      .sort((a: LocatieKort, b: LocatieKort) => nummerVan(a.nummer) - nummerVan(b.nummer)),
    kansen: (kansen.data ?? []).map((k: any): KansKort => ({ id: k.id, naam: tekst(k.name) ?? '(zonder naam)', status: k.status ?? null, vloot: getal(k.total_fleet), elektrisch: getal(k.electric_trucks) })),
    kavels: (kavels.data ?? []).map((k: any): KavelKort => ({ id: k.id, naam: tekst(k.name) ?? '(zonder naam)', fase: k.stage ?? null, plaats: tekst(k.city) })),
    /* piek_analyses is optioneel: een fout daar maakt de pagina niet stuk */
    piekanalyses: piek.error ? 0 : (piek.data ?? []).length,
  };
}

/* ── contact ──────────────────────────────────────────────────────── */

export interface Contact {
  id: string;
  naam: string;
  functie: string | null;
  email: string | null;
  telefoon: string | null;
  mobiel: string | null;
  avg: boolean;
  zichtbaarVoor: string[];
  aangemaakt: string | null;
  bron: Record<string, any>;
}

export interface ContactOrg {
  id: string;
  naam: string;
  sector: string | null;
  status: string | null;
  classificatie: string | null;
  logo: string | null;
  gearchiveerd: boolean;
}

export interface ContactPaginaData {
  contact: Contact;
  organisatie: ContactOrg | null;
  collegas: ContactKort[];
  offertes: OfferteKort[];
  projecten: ProjectKort[];
}

export async function haalContact(client: RelatieClient, id: string): Promise<ContactPaginaData> {
  const c = await client.from('contacts').select('*').eq('id', id).maybeSingle();
  if (c.error) {
    if (c.error.code === '22P02') throw new RelatieFout('niet-gevonden', `"${id}" is geen geldig contact.`);
    throw new RelatieFout('fout', c.error.message ?? String(c.error));
  }
  if (!c.data) throw new RelatieFout('niet-gevonden', 'Dit contact bestaat niet, of je mag het niet zien.');
  const naam = contactNaam(c.data.first_name, c.data.last_name);
  if (c.data.deleted_at) throw new RelatieFout('gearchiveerd', `"${naam}" is gearchiveerd. Je kunt het contact terugzetten vanuit het Archief.`, c.data.deleted_at);

  const orgId: string | null = c.data.org_id ?? null;
  const leeg = Promise.resolve({ data: [], error: null });
  const [org, collegas, projecten] = await Promise.all([
    orgId ? client.from('organizations').select('id, name, sector, status, classification, logo_url, favicon_url, deleted_at').eq('id', orgId).maybeSingle() : Promise.resolve({ data: null, error: null }),
    orgId ? client.from('contacts').select(CONTACT_KORT).eq('org_id', orgId).neq('id', id).is('deleted_at', null) : leeg,
    orgId ? client.from('projects').select(PROJECT_KORT).eq('org_id', orgId).is('deleted_at', null) : leeg,
  ]);
  controleer(org, collegas, projecten);
  const offertes = await offertesVoor(client, { contact: id });

  const r = c.data;
  const o = org.data;
  return {
    contact: {
      id: r.id,
      naam,
      functie: tekst(r.title),
      email: tekst(r.email),
      telefoon: tekst(r.phone),
      mobiel: tekst(r.mobile),
      avg: Boolean(r.consent_gdpr),
      zichtbaarVoor: Array.isArray(r.visible_for_tenants) ? r.visible_for_tenants : [],
      aangemaakt: r.created_at ?? null,
      bron: r,
    },
    organisatie: o
      ? { id: o.id, naam: tekst(o.name) ?? '(zonder naam)', sector: tekst(o.sector), status: o.status ?? null, classificatie: o.classification ?? null, logo: o.logo_url || o.favicon_url || null, gearchiveerd: Boolean(o.deleted_at) }
      : null,
    collegas: opAchternaam(collegas.data ?? []).map(alsContact),
    offertes,
    projecten: (projecten.data ?? []).map(alsProject).sort(opNummer),
  };
}

/** Voor de pagina: laden, een van de fouten, of de gegevens. */
export type PaginaStaat<T> =
  | { soort: 'laden' }
  | { soort: RelatieFoutSoort; fout: RelatieFout }
  | { soort: 'klaar'; data: T };

export function paginaStaat<T>(q: { isPending: boolean; error: unknown; data: T | undefined }): PaginaStaat<T> {
  if (q.error) {
    const f = q.error instanceof RelatieFout ? q.error : new RelatieFout('fout', q.error instanceof Error ? q.error.message : String(q.error));
    return { soort: f.soort, fout: f };
  }
  if (q.isPending || q.data === undefined) return { soort: 'laden' };
  return { soort: 'klaar', data: q.data };
}
